import { apiErrorText } from "../../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `history` — filters, the list, and the detail panel with
 * audio, summary, transcript and the "what happened" event log.
 *
 * EVERY FILTER IS IN THE URL. The range, the outcome and the search text are typed search
 * parameters on the route, not component state, so "the calls that went wrong yesterday" is
 * a link someone can paste into a message. That is the whole reason P10 asked for TanStack
 * Router's typed search: this screen's state is the thing people want to share.
 *
 * THE SEARCH BOX TYPES LOCALLY AND WRITES THE URL WHEN THE TYPING STOPS (review #121). Writing
 * every keystroke to the URL re-keyed the list's query, the tab fell back to its loading line,
 * the box unmounted and the next keystroke went nowhere: "6947130688" became "6". The box now
 * owns what is being typed, the URL follows 350 ms after the last keystroke, and the list keeps
 * showing the previous page while the next one loads, so nothing ever unmounts under the cursor.
 *
 * The detail panel's four sections are the four questions a customer actually asks about a
 * finished conversation: what did it sound like, what happened in a sentence, what was
 * said, and what did the platform do. The last one is the one no competitor shows and the
 * one that settles arguments — each event in plain words (review #85): the question the agent
 * could not answer, the order it placed, the presence check, the goodbye.
 *
 * MONEY IS WHAT BILLING CHARGES (review #109). A row's and the figures' money is the seconds the
 * free trial did not cover; where the trial covered some, the list price is named beside it
 * ("€1.01 at list price, covered by your free minutes") rather than shown as a charge.
 *
 * AMENDED 2026-10-12 (plan-017 WAPP-B, design §29.11 row 10, owner answer OQ-1): a conversation's money is the rate it
 * PINNED at admission (or, from before the talk rate cutover, the rate it was priced at), never the rate now. The
 * detail names what it was priced as — "Calls to your WeTalk number · <rate> a minute" — from the row's talk rate kind
 * and rate; a test reads "Test — free" (owner answer Q4); one with billed seconds and no price reads "Price
 * unavailable" and is never given a guessed figure.
 *
 * WEB CHAT AND BROWSER VOICE (plan-004 WC-90). Every row says how it came in — Phone, Website chat,
 * Website voice, Browser test, Test chat — from the API's `channel_label`, and the Channel chips
 * filter by it (only the labels this agent's conversations carry are offered). A website visitor is
 * "Visitor 8F22A1", never the key in `party_address`; a visitor who wrote before links to all their
 * conversations on that widget ("2 conversations from this visitor", the URL's `party`). A typed
 * conversation shows its turns and says it has no recording because it was text; the console's test
 * chat reads "Free"; a browser test is billed like any voice test and says so.
 *
 * WEB CHAT + VOICE (plan-005 OP-44, D9). A widget conversation in which the visitor both typed and
 * talked is ONE row, "Web chat + voice", with its whole duration and charge; the "Website chat" and
 * "Website voice" chips both list it and it has no chip of its own (the API offers the two). Its
 * detail marks every transcript line Voice or Text, puts a switch marker — its time, and the part's
 * talk time — where each part begins, notes which voice part was recorded, and splits the billed
 * figure per medium by OP-24's rule (the free trial covers the voice seconds first). A single-mode
 * conversation renders exactly as before: none of this appears for it.
 *
 * YOUR OWN SIP TRUNK (plan-008 ST-UA, design §22.10.2, §22.13.4, §22.14). A conversation that came
 * through the account's own SIP trunk carries ST-AN's `sip_leg`, and its detail says so: the trunk's
 * name and the direction; the voice quality in the honest words — `HD (G.722, 16 kHz)`, `Standard
 * (G.711 A-law, 8 kHz)`, or "Codec not reported" when the SIP edge did not report one, never a
 * default — with the HD sentence as its note; the connection (transport and whether the audio was
 * encrypted), each "not reported" when unobserved; and for an outbound conversation how it was
 * started (Ring a number now, a campaign, a test call) and the member's own reference.
 *
 * MESSAGES (plan-018 WAPP-B, design §30.12, ADR 0060). The "Messages" chip lists every conversation in which a caller
 * left a message — taken in the conversation, after hours or while every line was busy (`outcome=message_taken`); such a
 * row carries a "Message" tag. The detail shows each message as one card (name, contact — "Not confirmed" when a spoken
 * contact did not match — message, summary, time; "Hidden for your role" where the reader's role does not read them),
 * and a conversation in which the caller asked for a person says how that went ("Handed to a person" and the summary the
 * person heard). The notice in the notification centre opens here, on the conversation, with the chip on.
 *
 * PASSED BETWEEN AGENTS (plan-021 WAPP-HIST, design §33.13.3, OD-16, OD-7). The list shows every conversation this
 * VoiceAgent acted in — the ones passed in to it from another VoiceAgent of the workspace too — and a row that more than
 * one VoiceAgent answered says so ("2 agents"). The figures stay the conversations it ENTERED (money stays with the
 * VoiceAgent that answered first) and say how many were passed in ("1 passed in from another agent"). The detail draws a
 * divider where each VoiceAgent took over ("Passed to Delivery · With a summary", "Passed from Customer support", "Back
 * with Customer support"), names each agent line by the VoiceAgent that said it, says each one's outcome ("Outcome with
 * Delivery: Order placed"), what the next one was told ("What Delivery was told" — only for a reader who reads
 * transcripts; nothing is drawn otherwise) and each pass that could not happen ("Could not pass to Delivery: …"). A
 * conversation one VoiceAgent answered renders exactly as before: none of this appears for it.
 *
 * CALLER PROTECTION (plan-029 WAPP-HIST1, design §40.12, §40.13.1, §40.19.6; FR-CP-4, FR-CP-13). A conversation caller
 * protection refused or ended carries its reason as its tag — "Refused: blocked number", "Ended: the caller said
 * nothing" — and "What happened" words its `party_protection` events (the reason, the window, whether the caller was told
 * why; never a number). Two chips list them: "Refused" (the five refusals) and "Ended by caller protection" (the two
 * ends), sent as `end_reason`; one reason on its own (a notice's link) is its own chip. Where the detail's `may_block` is
 * true, "Block this number" blocks the conversation's caller after a confirm dialog, naming the conversation, never the
 * number, and says the last three digits at most.
 *
 * VOICEMAIL (plan-031 VMD-WAPP, design §42.11; FR-VMD-14). An outbound conversation a voicemail answered, and the runtime
 * hung up on at once, is tagged "Voicemail" in place of "No outcome"; its chip asks by end reason (`end_reason=voicemail`),
 * like caller protection's, and "What happened" words its `voicemail_detection` event ("Voicemail detected: a beep").
 *
 * CALLER CONFIRMATION (plan-029 WAPP-HIST2, design §40.12.2, §40.12.3, §40.13.2; FR-CI-16; AC-20). The detail says how
 * the caller confirmed who they were — "Caller confirmed by text message code at 14:02" — or "Could not confirm the
 * caller", with the codes sent and the wrong entries, and "What happened" words its `party_confirmation` events
 * ("Code didn't match (2 tries left) · keyed in"). Never a code, a passcode or a number: the api sends none.
 *
 * NOT BUILT, AND DRAWN DISABLED WHERE THE DESIGN PUTS THEM: "Custom range" (the range is one
 * of five typed values in the URL), "Export CSV" (no history export operation), and "Flag for
 * review" (a member cannot raise a conversation_flag through the API yet).
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Icon, Input, Segmented, Tag, type PhosphorIcon } from "@wetalk/ui";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { LanguageBetaTag, isBetaLanguage } from "../../../shell/language-beta.js";
import { useGateway } from "../../../api/provider.js";
import {
  VOICEMAIL_END_REASON,
  channelLabelWordOf,
  farEndWordOf,
  formatClock,
  isTextChannelLabel,
  isWebsiteChannelLabel,
  languageNameOf,
  lineModeWordOf,
  mediumSplitTextOf,
  outcomeLabel,
  partyLabelOf,
  segmentMarkerTextOf,
  trialNoteOf,
  voicemailReasonText,
} from "../../../api/conversation-view.js";
import {
  conversationSurveyAnswerQuery,
  languagePackQuery,
  numberQuery,
  pastConversationPageQuery,
  pastConversationQuery,
  voiceAgentListQuery,
} from "../../../api/query.js";
import { formatDuration, formatMoney } from "../../../money.js";
import { talkRateKindLabel, talkRateText } from "../../../api/slice/talk-rate.js";
import type { SipLegSummaryView } from "../../../api/slice/outbound-conversation.js";
import { HD_SENTENCE, TRANSPORT_WORD, codecWords } from "../../numbers/sip-trunk-copy.js";
import { displayNumberOf } from "../../numbers/number-display.js";
import { ConversationSurveyAnswer } from "./conversation-survey-answer.js";
import { HandoffCard, TakenMessageCard, TakenMessageRowTag } from "./message-card.js";
import { TAKEN_MESSAGE_OUTCOME_FILTER } from "../../../api/taken-message-view.js";
import {
  passedInTextOf,
  voiceAgentCountTextOf,
  voiceAgentNameOf,
  voiceAgentPassSentenceOf,
  voiceAgentPassTranscriptOf,
  type VoiceAgentPassTranscriptEntry,
} from "../../../api/voice-agent-stint-view.js";
import { VoiceAgentPassLine } from "../../test-console/voice-agent-pass-line.js";
import {
  PARTY_ENDED_FILTER,
  PARTY_REFUSED_FILTER,
  isPartyProtectionEndReason,
  isPartyRefusalEndReason,
  partyProtectionReasonText,
} from "../../../api/party-protection-view.js";
import { BlockThisNumber } from "./block-number-dialog.js";
import { partyConfirmationCountsOf, partyConfirmationSummaryOf } from "../../../api/party-confirmation-view.js";
import type {
  ChannelLabel,
  ConversationSegmentView,
  HistoryChannelLabelFilter,
  HistoryOutcomeFilter,
  HistoryRange,
  LanguagePackView,
  PastConversationRowView,
  PastConversationView,
  TranscriptLineView,
} from "../../../api/gateway.js";

declare module "../../../api/gateway.js" {
  interface PastConversationView {
    /**
     * plan-008 (ST-AN's `SipLegSummary`): present only for a conversation through the account's own
     * SIP trunk. Optional until the api gateway's `getPastConversation` decodes `sip_leg` (hand-off to
     * the gateway's owner); a conversation without it renders exactly as before.
     */
    readonly sipLeg?: SipLegSummaryView | null;
  }
}

/** How an outbound trunk conversation was started, in words. */
const OUTBOUND_ORIGIN_WORD: Readonly<Record<NonNullable<SipLegSummaryView["outboundOrigin"]>, string>> = {
  get on_demand() { return portalText("operations", "ring_a_number_now_63f76955"); },
  get campaign() { return portalText("operations", "a_campaign_e500ef1e"); },
  get test_call() { return portalText("operations", "a_test_call_1dccdc93"); },
};

/** `Our VoIP line · outgoing` — the trunk and the direction. */
export function sipLegTrunkText(leg: SipLegSummaryView): string {
  return `${leg.sipTrunkDisplayName} · ${leg.direction === "inbound" ? portalText("operations", "incoming_2ff8dd80") : portalText("operations", "outgoing_708a91a8")}`;
}

/** `TLS · audio encrypted (SRTP)` — each half "not reported" when it was not observed. */
export function sipLegConnectionText(leg: SipLegSummaryView): string {
  const transport = leg.transport === null ? portalText("operations", "transport_not_reported_188ca4a8") : TRANSPORT_WORD[leg.transport];
  const encryption =
    leg.mediaEncryption === null
      ? portalText("operations", "encryption_not_reported_433e6c9f")
      : leg.mediaEncryption === "srtp"
        ? portalText("operations", "audio_encrypted_srtp_b200aef6")
        : portalText("operations", "audio_not_encrypted_3459e08b");
  return `${transport} · ${encryption}`;
}

/** `Ring a number now · reference "Invoice 2211"`, or `null` for an inbound leg. */
export function sipLegOriginText(leg: SipLegSummaryView): string | null {
  if (leg.outboundOrigin === null) return null;
  const origin = OUTBOUND_ORIGIN_WORD[leg.outboundOrigin];
  return leg.outboundReference === null ? origin : portalText("operations", "value_reference_value_ac8d5f0b", { v0: String(origin), v1: String(leg.outboundReference) });
}

/** The design's five windows. "90d" stays a valid URL value; it is not offered. */
const RANGE_OPTION = [
  { value: "today", get label() { return portalText("operations", "today_24345a14"); } },
  { value: "yesterday", get label() { return portalText("operations", "yesterday_da24830f"); } },
  { value: "7d", get label() { return portalText("operations", "7_days_0a8dd6ad"); } },
  { value: "30d", get label() { return portalText("operations", "30_days_7d4278a8"); } },
  { value: "all", get label() { return portalText("operations", "all_6a720856"); } },
] as const;

/** How many days back each range starts, for the span beside the control. */
const RANGE_START_DAY: Readonly<Record<HistoryRange, number | null>> = {
  today: 0,
  yesterday: 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
  all: null,
};

const MONTH_NAME = () => ([
  portalText("operations", "january_7a22d73d"), portalText("operations", "february_5c3a35ef"), portalText("operations", "march_433632ea"), portalText("operations", "april_a0393902"), portalText("operations", "may_c94f4798"), portalText("operations", "june_a9db9067"),
  portalText("operations", "july_df97a425"), portalText("operations", "august_69d97c57"), portalText("operations", "september_1c542e79"), portalText("operations", "october_87206ae2"), portalText("operations", "november_3c5bf776"), portalText("operations", "december_65640c65"),
] as const);

/** How long the search box waits after the last keystroke before it writes the URL. */
export const SEARCH_SETTLE_MS = 350;

/**
 * The window in words, as the design writes it: `13–20 September`, `19 September`.
 *
 * Compared by calendar day, never by instant (review #113: "Today" read "25–25 September",
 * because `today` carried the clock time and the window's first day did not).
 */
export function rangeSpanText(range: HistoryRange, today: Date): string {
  const back = RANGE_START_DAY[range];
  if (back === null) return "";
  const day = (at: Date): string => `${String(at.getDate())} ${MONTH_NAME()[at.getMonth()] ?? ""}`;
  const to = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (range === "yesterday" ? 1 : 0));
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - back);
  if (from.getTime() === to.getTime()) return day(to);
  if (from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth()) {
    return `${String(from.getDate())}–${day(to)}`;
  }
  return `${day(from)} – ${day(to)}`;
}

/**
 * The outcome chips (review #113, #122): All, Messages (plan-018), then the outcomes this agent's conversations have
 * actually reached in the template's own words ("Order placed"), then Transferred and No outcome.
 * "All" is how a chosen chip is cleared. An outcome in the URL that no conversation carries any
 * more stays offered, so the chip that is on is always visible.
 */
export function outcomeChipsOf(
  outcomeOption: readonly string[],
  current: HistoryOutcomeFilter,
): readonly { readonly value: HistoryOutcomeFilter; readonly label: string }[] {
  const fixed = new Set(["all", "reached", "transferred", "none", "completed", "abandoned", TAKEN_MESSAGE_OUTCOME_FILTER, PARTY_REFUSED_FILTER, PARTY_ENDED_FILTER, VOICEMAIL_END_REASON]);
  const words = outcomeOption.filter((one) => !fixed.has(one));
  // plan-029: one caller-protection reason on its own (a notice's link) is offered in its own words, after the groups.
  const partyReason = isPartyProtectionEndReason(current) ? current : null;
  if (!fixed.has(current) && partyReason === null && !words.includes(current)) words.push(current);
  return [
    { value: "all", label: portalText("operations", "all_6a720856") },
    // plan-018 WAPP-B: every conversation in which a caller left a message (a code, never a template's word).
    { value: TAKEN_MESSAGE_OUTCOME_FILTER, label: portalText("operations", "taken_message.filter_messages") },
    ...words.map((one) => ({ value: one, label: one })),
    ...(current === "reached" ? [{ value: "reached", label: portalText("operations", "any_outcome_e39a360a") }] : []),
    { value: "transferred", label: portalText("operations", "transferred_f76d1e2a") },
    { value: "none", label: portalText("operations", "no_outcome_a4fe5d40") },
    // plan-029 WAPP-HIST1: what caller protection refused, and what it ended (codes, never a template's word).
    { value: PARTY_REFUSED_FILTER, label: portalText("callerProtection", "history.filter_refused") },
    { value: PARTY_ENDED_FILTER, label: portalText("callerProtection", "history.filter_ended") },
    // plan-031 VMD-WAPP: the outbound conversations a voicemail answered (an end reason, never a template's word).
    { value: VOICEMAIL_END_REASON, label: voicemailReasonText() },
    ...(partyReason === null ? [] : [{ value: partyReason, label: partyProtectionReasonText(partyReason) }]),
  ];
}

/**
 * The channel chips (plan-004 WC-90): All, then every label this agent's conversations carry, in
 * the API's order (most frequent first). A label in the URL that no conversation carries any more
 * stays offered, so the chip that is on is always visible.
 */
export function channelLabelChipsOf(
  channelLabelOption: readonly ChannelLabel[],
  current: HistoryChannelLabelFilter,
): readonly { readonly value: HistoryChannelLabelFilter; readonly label: string }[] {
  const labels: ChannelLabel[] = [...channelLabelOption];
  if (current !== "all" && !labels.includes(current)) labels.push(current);
  return [
    { value: "all", label: portalText("operations", "all_6a720856") },
    ...labels.map((one) => ({ value: one, label: channelLabelWordOf(one) })),
  ];
}

/**
 * The transcript's speaker, in the customer's words. `assistant` is the glossary's label; the far
 * end is a caller on the phone, a visitor on the website, and you in a test (plan-004 WC-90).
 */
export function speakerWordOf(speaker: TranscriptLineView["speaker"], channelLabel: ChannelLabel, survey = false): string {
  if (speaker === "assistant") return portalText("operations", "agent_5ce2e6f4");
  if (speaker === "system") return portalText("operations", "system_bc0792d8");
  // Design 2026-10-15: a survey agent's far end on a call is "Person called", not "Caller".
  if (survey && !isWebsiteChannelLabel(channelLabel) && channelLabel !== "browser_test" && channelLabel !== "test_chat") {
    return portalText("operations", "history.person_called");
  }
  return farEndWordOf(channelLabel);
}

/** A row's glyph: how it came in. */
function channelLabelGlyphOf(row: Pick<PastConversationRowView, "channelLabel" | "direction">): PhosphorIcon {
  switch (row.channelLabel) {
    case "web_chat":
      return GLYPH.chatText;
    case "test_chat":
      return GLYPH.chatCircleDots;
    case "website_voice":
      return GLYPH.browser;
    case "web_chat_with_voice":
      return GLYPH.waveform;
    case "browser_test":
      return GLYPH.headset;
    case "whatsapp":
      return GLYPH.whatsappLogo;
    case "viber":
      return GLYPH.chatCircleDots;
    case "phone":
      return row.direction === "outbound" ? GLYPH.phoneOutgoing : GLYPH.phoneCall;
  }
}

/**
 * What the detail says where the player would be when there is none. A typed conversation never has
 * a recording, and saying "your role may not include recordings" about a web chat would be untrue.
 */
export function noRecordingTextOf(channelLabel: ChannelLabel): string {
  if (isTextChannelLabel(channelLabel)) return portalText("operations", "no_recording_this_conversation_was_typed_not_spoken_a8d8ca51");
  return portalText("operations", "no_recording_either_there_is_none_retention_has_already_de_69414037");
}

/** One entry of a mixed conversation's transcript: a line, or the marker where a part begins. */
export type MixedTranscriptEntry =
  | { readonly kind: "line"; readonly line: TranscriptLineView; readonly index: number }
  | { readonly kind: "switch"; readonly segment: ConversationSegmentView };

/**
 * A mixed conversation's transcript with its switch markers (plan-005 OP-44): each part's marker goes
 * before the first line said at or after the part began, in the order the lines were stored; a part
 * nobody spoke in (a voice leg hung up at once) keeps its marker, after the lines before it. A line
 * without a time (an api from before OP-44) never moves a marker.
 */
export function mixedTranscriptOf(
  transcript: readonly TranscriptLineView[],
  segment: readonly ConversationSegmentView[],
): readonly MixedTranscriptEntry[] {
  const pending = [...segment].sort((left, right) => left.ordinal - right.ordinal);
  const entry: MixedTranscriptEntry[] = [];
  transcript.forEach((line, index) => {
    const saidAtMs = line.startedAt === undefined ? Number.NaN : Date.parse(line.startedAt);
    while (!Number.isNaN(saidAtMs) && pending.length > 0 && Date.parse(pending[0]?.startedAt ?? "") <= saidAtMs) {
      const next = pending.shift();
      if (next !== undefined) entry.push({ kind: "switch", segment: next });
    }
    entry.push({ kind: "line", line, index });
  });
  for (const rest of pending) entry.push({ kind: "switch", segment: rest });
  return entry;
}

/** A voice part's marker says whether it was recorded; a text part's never does. */
export function switchMarkerTextOf(segment: ConversationSegmentView): string {
  const marker = segmentMarkerTextOf(segment);
  return segment.recording === null ? marker : portalText("operations", "value_recorded_104d284e", { v0: String(marker) });
}

/** "2 conversations from this visitor", or `null` when this is the party's only one. */
export function partyConversationTextOf(
  one: Pick<PastConversationRowView, "partyConversationCount" | "channelLabel">,
): string | null {
  if (one.partyConversationCount < 2) return null;
  const who = isWebsiteChannelLabel(one.channelLabel) ? portalText("operations", "this_visitor_055271bf") : portalText("operations", "this_caller_e029bb49");
  return portalText("operations", "value_conversations_from_value_9be75e5d", { v0: String(String(one.partyConversationCount)), v1: String(who) });
}

/**
 * The waveform's bar scales: decoration, fixed, so two renders never differ. Each travels as
 * a custom property, the way every meter's fill does, and the stylesheet draws the bar.
 */
const WAVE: readonly CSSProperties[] = Array.from(
  { length: 26 },
  (_, index) => ({ "--c-wave": String(0.2 + ((index * 7) % 10) / 12) }) as CSSProperties,
);

function languageName(code: string | null, pack: readonly LanguagePackView[] | undefined): string {
  if (code === null) return "—";
  return pack?.find((one) => one.code === code)?.name ?? languageNameOf(code);
}

/** "Inbound" / "Outbound", as the design's History row reads. */
function directionWordOf(direction: PastConversationRowView["direction"]): string {
  return direction === "outbound"
    ? portalText("operations", "history.direction_outbound")
    : portalText("operations", "history.direction_inbound");
}

export interface HistoryTabProps {
  readonly voiceAgentId: string;
  readonly range: HistoryRange;
  readonly outcome: HistoryOutcomeFilter;
  readonly search: string;
  /** The channel chip (plan-004 WC-90). */
  readonly channelLabel: HistoryChannelLabelFilter;
  /** One party's conversations, named by one of them; `""` is everyone. */
  readonly partyOf: string;
  readonly selectedConversationId: string;
  readonly onRange: (value: HistoryRange) => void;
  readonly onOutcome: (value: HistoryOutcomeFilter) => void;
  readonly onSearch: (value: string) => void;
  readonly onChannelLabel: (value: HistoryChannelLabelFilter) => void;
  /** Show one party's conversations (a conversation id), or everyone again (`""`). */
  readonly onParty: (conversationId: string) => void;
  /**
   * numbers-sip-sync: one number's calls, named by its `phone_number_id` ("See all calls on this number"); absent or
   * `""` is every number. `onNumber("")` shows every call again.
   */
  readonly phoneNumberId?: string;
  readonly onNumber?: (phoneNumberId: string) => void;
  readonly onSelect: (conversationId: string) => void;
  /**
   * A survey VoiceAgent's History (2026-10-03): the detail shows the conversation's answers, statement
   * by statement, above its summary. Absent is every other VoiceAgent.
   */
  readonly surveyAnswer?: boolean;
}

/**
 * The search box: what is typed is the box's own state; the URL hears about it once the typing
 * settles. A change to the URL from elsewhere ("Show all", a pasted link) resets the box.
 */
export function HistorySearchBox({
  search,
  onSearch,
}: {
  readonly search: string;
  readonly onSearch: (value: string) => void;
}): ReactNode {
  usePortalTranslation("operations");
  const [typed, setTyped] = useState(search);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastWritten = useRef(search);

  useEffect(() => {
    // Only a change that did not come from this box moves the text under the cursor.
    if (search !== lastWritten.current) {
      lastWritten.current = search;
      setTyped(search);
    }
  }, [search]);

  useEffect(
    () => () => {
      if (pending.current !== null) clearTimeout(pending.current);
    },
    [],
  );

  return (
    <label className="c-va-search c-va-search-narrow">
      <span className="c-va-search-icon" aria-hidden="true">
        <Icon glyph={GLYPH.magnifyingGlass} size="md" />
      </span>
      <Input
        type="search"
        aria-label={portalText("operations", "search_conversations_70878885")}
        placeholder={portalText("operations", "number_outcome_or_conversation_id_9fc99a6e")}
        value={typed}
        onChange={(event) => {
          const next = event.currentTarget.value;
          setTyped(next);
          if (pending.current !== null) clearTimeout(pending.current);
          pending.current = setTimeout(() => {
            pending.current = null;
            lastWritten.current = next;
            onSearch(next);
          }, SEARCH_SETTLE_MS);
        }}
      />
    </label>
  );
}

export function HistoryTab(props: HistoryTabProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  // Read once, when the tab opens: the span is a label, not a clock.
  const [today] = useState(() => new Date());
  const languagePack = useQuery(languagePackQuery(gateway));
  const page = useQuery({
    ...pastConversationPageQuery(gateway, {
      voiceAgentId: props.voiceAgentId,
      range: props.range,
      outcome: props.outcome,
      search: props.search,
      channelLabel: props.channelLabel,
      partyOf: props.partyOf,
      phoneNumberId: props.phoneNumberId ?? "",
    }),
    // The previous page stays on screen while the next filter's loads, so the controls above
    // it never unmount (review #121).
    placeholderData: keepPreviousData,
  });

  const selected =
    page.data === undefined
      ? undefined
      : (page.data.row.find((one) => one.conversationId === props.selectedConversationId) ??
        page.data.row[0]);

  return (
    <>
      <div className="c-va-history-controls">
        <Segmented
          name="historyRange"
          legend={portalText("operations", "range_a16c5bbe")}
          value={props.range}
          options={RANGE_OPTION}
          onChange={props.onRange}
        />
        <span className="c-va-history-note">{rangeSpanText(props.range, today)}</span>
        <span className="c-va-push">
          <Button variant="ghost" disabled>
            <Icon glyph={GLYPH.calendarBlank} size="md" />
            <span>{portalText("operations", "custom_range_soon_eba5045a")}</span>
          </Button>
        </span>
      </div>

      {page.data !== undefined &&
      (page.data.channelLabelOption.length > 1 || props.channelLabel !== "all") ? (
        <div className="c-va-history-controls c-va-history-controls-loose">
          <Segmented
            name="historyChannel"
            legend={portalText("operations", "channel_879f0b1b")}
            value={props.channelLabel}
            options={channelLabelChipsOf(page.data.channelLabelOption, props.channelLabel)}
            onChange={props.onChannelLabel}
          />
        </div>
      ) : null}

      {props.phoneNumberId === undefined || props.phoneNumberId === "" ? null : (
        <NumberFilterNote
          phoneNumberId={props.phoneNumberId}
          onClear={() => {
            props.onNumber?.("");
          }}
        />
      )}

      {props.partyOf === "" ? null : (
        <p className="c-note" role="status">
          {portalText("operations", "showing_one_visitor_s_conversations_all_time_ade4b7ff")}{" "}
          <Button
            variant="ghost"
            onClick={() => {
              props.onParty("");
            }}
          >
            {portalText("operations", "show_everyone_284369fb")}</Button>
        </p>
      )}

      <div className="c-va-history-controls c-va-history-controls-loose">
        <Segmented
          name="historyOutcome"
          legend={portalText("operations", "outcome_d3f06106")}
          value={props.outcome}
          options={outcomeChipsOf(page.data?.outcomeOption ?? [], props.outcome)}
          onChange={props.onOutcome}
        />
        <HistorySearchBox search={props.search} onSearch={props.onSearch} />
        <span className="c-va-push">
          <Button variant="secondary" disabled>
            <Icon glyph={GLYPH.downloadSimple} size="md" />
            <span>{portalText("operations", "export_csv_soon_74f9a7dc")}</span>
          </Button>
        </span>
      </div>

      {page.data === undefined ? (
        page.isError ? (
          <p className="c-note c-note-critical" role="alert">
            {portalText("operations", "the_history_could_not_be_read_84878e50")}{apiErrorText(page.error, "")}
          </p>
        ) : (
          <p className="c-note">{portalText("operations", "reading_the_history_3674d882")}</p>
        )
      ) : (
        <>
          <div className="c-va-history-stats" aria-busy={page.isPlaceholderData}>
            {page.data.stat.map((one) => (
              <div className="c-va-orders-stat" key={one.label} title={one.note ?? undefined}>
                <span className="c-va-orders-stat-value">{one.value}</span>
                <span className="c-va-orders-stat-label">{one.label}</span>
              </div>
            ))}
          </div>
          {/* plan-021: the listed conversations passed in from another VoiceAgent, counted in no figure above. */}
          {passedInTextOf(page.data.passedInConversationCount) === null ? null : (
            <p className="c-va-history-note">{passedInTextOf(page.data.passedInConversationCount)}</p>
          )}

          <div className="c-va-history">
            <div className="c-va-history-list">
              {page.data.row.length === 0 ? (
                <p className="c-va-history-empty">
                  {portalText("operations", "no_conversations_match_that_filter_d09ae5a3")}{" "}
                  <Button
                    variant="ghost"
                    onClick={() => {
                      props.onOutcome("all");
                      props.onSearch("");
                      props.onChannelLabel("all");
                      props.onParty("");
                    }}
                  >
                    {portalText("operations", "show_all_50a279de")}</Button>
                </p>
              ) : (
                page.data.row.map((row) => (
                  <HistoryRow
                    key={row.conversationId}
                    row={row}
                    pack={languagePack.data}
                    current={row.conversationId === selected?.conversationId}
                    onSelect={props.onSelect}
                  />
                ))
              )}
            </div>

            {selected === undefined ? null : (
              <ConversationDetail
                key={selected.conversationId}
                conversationId={selected.conversationId}
                voiceAgentId={props.voiceAgentId}
                pack={languagePack.data}
                partyShown={props.partyOf !== ""}
                onParty={props.onParty}
                surveyAnswer={props.surveyAnswer === true}
              />
            )}
          </div>
        </>
      )}
    </>
  );
}

function OutcomeTag({ row }: { readonly row: PastConversationRowView }): ReactNode {
  usePortalTranslation("operations");
  // plan-029: a conversation caller protection refused says why in place of "No outcome" (it was never answered); one it
  // ended keeps its outcome and says why it ended beside it.
  if (isPartyRefusalEndReason(row.endReason)) {
    return <Tag status="caution">{partyProtectionReasonText(row.endReason)}</Tag>;
  }
  // plan-031 VMD-WAPP: a voicemail answered and was hung up on before a word — it says so in place of "No outcome".
  if (row.endReason === VOICEMAIL_END_REASON) return <Tag status="muted">{voicemailReasonText()}</Tag>;
  return (
    <>
      <Tag status={row.endReason === "transferred" ? "muted" : row.outcome === null ? "muted" : "positive"}>
        {outcomeLabel(row.outcome)}
      </Tag>
      {isPartyProtectionEndReason(row.endReason) ? (
        <Tag tone="outline">{partyProtectionReasonText(row.endReason)}</Tag>
      ) : null}
    </>
  );
}

/**
 * A row's money: what billing charges, and "free" when the trial covered all of it. The console's
 * test chat is never billed (design §17.9.3) and says so, rather than "€0.00" as if it were a charge
 * that happened to round to nothing.
 */
export function chargedText(
  row: Pick<
    PastConversationRowView,
    "channelLabel" | "billedSeconds" | "chargedSeconds" | "listAmount" | "billedAmount" | "isTest" | "talkRatePerMinute" | "priceUnavailable"
  >,
): string {
  if (row.channelLabel === "test_chat") return portalText("operations", "free_test_chats_are_not_billed_376f80ed");
  if (row.priceUnavailable) return portalText("operations", "history.price_unavailable");
  if (isPricedAtZeroAsIsTest(row)) return portalText("operations", "history.test_free");
  if (row.billedSeconds > 0 && row.chargedSeconds === 0 && row.listAmount.amountMinor > 0) {
    return portalText("operations", "value_free_minutes_ed11e310", { v0: String(formatMoney(row.billedAmount)) });
  }
  return formatMoney(row.billedAmount);
}

/** A test priced at 0 — every test pinned after the talk rate cutover (owner answers Q4 and PQ-5). */
function isPricedAtZeroAsIsTest(row: Pick<PastConversationRowView, "isTest" | "talkRatePerMinute">): boolean {
  return row.isTest && row.talkRatePerMinute !== null && row.talkRatePerMinute.amountMinor === 0;
}

/**
 * What the conversation was priced as: "Calls to your WeTalk number · <rate> a minute" — its kind's label and the rate
 * it pinned (design §29.11 row 10). Only the rate for one from before per-kind rates (no kind). `null` when there is
 * nothing to name: no price, a free test (its money already says so), or a test chat.
 */
export function talkRateFactOf(
  row: Pick<PastConversationRowView, "isTest" | "talkRateKind" | "talkRatePerMinute" | "priceUnavailable">,
): string | null {
  if (row.priceUnavailable || row.talkRatePerMinute === null || isPricedAtZeroAsIsTest(row)) return null;
  const rate = talkRateText(row.talkRatePerMinute, "minute");
  return row.talkRateKind === null ? rate : `${talkRateKindLabel(row.talkRateKind)} · ${rate}`;
}

function HistoryRow({
  row,
  pack,
  current,
  onSelect,
}: {
  readonly row: PastConversationRowView;
  readonly pack: readonly LanguagePackView[] | undefined;
  readonly current: boolean;
  readonly onSelect: (conversationId: string) => void;
}): ReactNode {
  usePortalTranslation("operations");
  return (
    <button
      type="button"
      className="c-va-history-row"
      aria-current={current}
      onClick={() => {
        onSelect(row.conversationId);
      }}
    >
      <span className="c-va-history-row-head">
        <span className="c-va-history-row-icon" title={channelLabelWordOf(row.channelLabel)}>
          <Icon glyph={channelLabelGlyphOf(row)} size="lg" />
        </span>
        <span className="c-va-history-row-party">{partyLabelOf(row)}</span>
        <OutcomeTag row={row} />
        {row.hasTakenMessage === true ? <TakenMessageRowTag /> : null}
        {row.isTest ? <Tag tone="outline">{portalText("operations", "test_a94a8fe5")}</Tag> : null}
        {/* plan-021: more than one VoiceAgent answered it. */}
        {voiceAgentCountTextOf(row.voiceAgentCount) === null ? null : (
          <Tag tone="outline">{voiceAgentCountTextOf(row.voiceAgentCount)}</Tag>
        )}
      </span>
      <span className="c-va-history-row-meta">
        <span>{row.whenText}</span>
        <span>
          {row.channelLabel === "phone" ? directionWordOf(row.direction) : channelLabelWordOf(row.channelLabel)} ·{" "}
          {languageName(row.languageCode, pack)}
          {/* plan-026 (0176): a beta language carries its Beta badge. */}
          {isBetaLanguage(pack, row.languageCode) ? (
            <>
              {" "}
              <LanguageBetaTag />
            </>
          ) : null}
        </span>
        <span
          className="c-va-tabular"
          title={
            [talkRateFactOf(row), trialNoteOf(row.billedSeconds, row.chargedSeconds, row.listAmount)]
              .filter((one) => one !== null)
              .join(" · ") || undefined
          }
        >
          {formatDuration(row.billedSeconds)} · {chargedText(row)}
        </span>
        <span>{row.agentVersionLabel ?? "—"}</span>
      </span>
    </button>
  );
}

/** A granted URL, reused only while it still works. */
interface GrantedPlay {
  readonly url: string;
  readonly expiresAtMs: number;
  /** The name the URL itself downloads as (a signed Content-Disposition), or `null` (review 122). */
  readonly fileName: string | null;
}

/**
 * Opens a URL the storage service answers with `Content-Disposition: attachment` (review 122, fix
 * round R): the browser saves it under the signed name and stays on the page. No fetch, so no CORS.
 */
export function openSignedDownload(url: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * A recording's file name (review #122): `call-2026-09-25-0330-306947130688.ogg`, the caller's
 * digits only where this reader may see them, the conversation id's first block otherwise.
 */
export function recordingFileNameOf(
  one: Pick<PastConversationView, "startedAt" | "partyAddress" | "conversationId">,
  blobName: string | null,
  codec: string | null,
): string {
  const at = new Date(one.startedAt);
  const two = (value: number): string => String(value).padStart(2, "0");
  const stamp = Number.isNaN(at.getTime())
    ? "undated"
    : `${String(at.getFullYear())}-${two(at.getMonth() + 1)}-${two(at.getDate())}-${two(at.getHours())}${two(at.getMinutes())}`;
  const who = one.partyAddress === null ? "" : one.partyAddress.replace(/\D/g, "");
  const tail = who === "" ? (one.conversationId.split("-")[0] ?? "conversation") : who;
  const fromBlob = blobName === null ? null : /\.([a-z0-9]{2,5})$/i.exec(blobName)?.[1] ?? null;
  const extension = fromBlob ?? (codec === "opus" ? "ogg" : (codec ?? "audio"));
  return `call-${stamp}-${tail}.${extension.toLowerCase()}`;
}

/**
 * Saves `url` as `fileName` (review #122: Download opened the blob in a new tab as `<uuid>.ogg`).
 * A cross-origin URL cannot be renamed by an `<a download>`, so the bytes are fetched and saved
 * from a local object URL. Where the storage refuses the read (no CORS), the URL is opened as
 * before rather than failing silently.
 */
async function saveAs(url: string, fileName: string): Promise<void> {
  try {
    const answered = await fetch(url);
    if (!answered.ok) throw new Error(String(answered.status));
    const local = URL.createObjectURL(await answered.blob());
    const anchor = document.createElement("a");
    anchor.href = local;
    anchor.download = fileName;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => {
      URL.revokeObjectURL(local);
    }, 10_000);
  } catch {
    window.open(url, "_blank", "noopener");
  }
}

/**
 * The recording player (plan-003 RC-46). Drawn as the prototype draws it — play, the wave, the
 * duration, download — and wired to design §8.8: the URL is asked for when somebody presses play or
 * download, because each grant writes a `recording.played` entry in the account's audit log, and is
 * reused while it lasts (five minutes) so pausing and resuming is one play, not several. A URL the
 * API already issued (the sandbox) is used as it is.
 */
export function RecordingPlayer({
  conversationId,
  issuedUrl,
  durationSeconds,
  fileName,
}: {
  readonly conversationId: string;
  readonly issuedUrl: string | null;
  readonly durationSeconds: number;
  /** What Download saves the file as; without one the URL is opened instead. */
  readonly fileName?: string;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const audio = useRef<HTMLAudioElement | null>(null);
  const granted = useRef<GrantedPlay | null>(null);
  const [playing, setPlaying] = useState(false);
  const [opening, setOpening] = useState(false);
  const [refused, setRefused] = useState(false);

  /** A grant that works now: the issued URL, a granted one still inside its window, or a new grant. */
  async function playable(): Promise<GrantedPlay | null> {
    if (issuedUrl !== null) return { url: issuedUrl, expiresAtMs: Number.POSITIVE_INFINITY, fileName: null };
    const held = granted.current;
    // A margin so a URL is never handed to the element seconds before it stops working.
    if (held !== null && held.expiresAtMs - Date.now() > 15_000) return held;
    setOpening(true);
    try {
      const access = await gateway.accessRecording(conversationId);
      const next = { url: access.url, expiresAtMs: Date.parse(access.expiresAt), fileName: access.fileName };
      granted.current = next;
      setRefused(false);
      return next;
    } catch {
      setRefused(true);
      return null;
    } finally {
      setOpening(false);
    }
  }

  async function playableUrl(): Promise<string | null> {
    return (await playable())?.url ?? null;
  }

  async function togglePlay(): Promise<void> {
    const element = audio.current;
    if (element === null) return;
    if (playing) {
      element.pause();
      setPlaying(false);
      return;
    }
    const url = await playableUrl();
    if (url === null) return;
    // Set on the element only. Mirroring it into React state re-rendered the element with a new
    // `src`, which restarts the load and ABORTS the pending play(): the first press did nothing
    // (review #83 re-test, 2026-09-25).
    if (element.getAttribute("src") !== url) element.src = url;
    try {
      await element.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  }

  async function download(): Promise<void> {
    const grant = await playable();
    if (grant === null) return;
    const url = grant.url;
    // Review 122 (fix round R): the API signs the name into the URL, so opening it saves
    // `call-….ogg` — no fetch, so no CORS on the recording container is needed.
    if (grant.fileName !== null) {
      openSignedDownload(url);
      return;
    }
    if (fileName === undefined) {
      window.open(url, "_blank", "noopener");
      return;
    }
    setOpening(true);
    try {
      await saveAs(url, fileName);
    } finally {
      setOpening(false);
    }
  }

  return (
    <>
      {/* The transcript below is this recording's caption, in full, and is the thing a screen
          reader should read — so the element carries no <track>. */}
      <audio
        ref={audio}
        preload="none"
        {...(issuedUrl === null ? {} : { src: issuedUrl })}
        onEnded={() => {
          setPlaying(false);
        }}
      />
      <button
        type="button"
        className="btn btn-icon"
        aria-label={playing ? portalText("operations", "pause_recording_9e7532c5") : portalText("operations", "play_recording_a5b28058")}
        disabled={opening}
        onClick={() => {
          void togglePlay();
        }}
      >
        <Icon glyph={playing ? GLYPH.pause : GLYPH.play} size="lg" />
      </button>
      <span className="c-va-wave" aria-hidden="true">
        {WAVE.map((style, index) => (
          <span key={`bar-${String(index)}`} className="c-va-wave-bar" style={style} />
        ))}
      </span>
      <span className="c-va-audio-duration">{formatDuration(durationSeconds)}</span>
      {issuedUrl !== null ? (
        <a
          className="btn btn-icon"
          href={issuedUrl}
          download={fileName ?? true}
          aria-label={portalText("operations", "download_recording_bf5579d2")}
        >
          <Icon glyph={GLYPH.downloadSimple} size="lg" />
        </a>
      ) : (
        // No URL exists until a play is granted (and audited), so download asks for one first.
        <button
          type="button"
          className="btn btn-icon"
          aria-label={portalText("operations", "download_recording_bf5579d2")}
          disabled={opening}
          onClick={() => {
            void download();
          }}
        >
          <Icon glyph={GLYPH.downloadSimple} size="lg" />
        </button>
      )}
      {refused ? (
        <p className="c-va-audio-note" role="status">
          {portalText("operations", "this_recording_could_not_be_opened_retention_may_have_dele_78c147ee")}</p>
      ) : null}
    </>
  );
}

/** How long "Transcript copied" stays before the button reads "Copy transcript" again. */
const COPIED_FOR_MS = 2_500;

/** "Answered" in a survey conversation's facts: how many answers it filed (design sync 2026-10-09). */
function AnsweredFact({ conversationId }: { readonly conversationId: string }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const read = useQuery(conversationSurveyAnswerQuery(gateway, conversationId));
  const count = read.data?.answer.length;
  return (
    <div>
      <dt>{portalText("operations", "answered_e0aafffa")}</dt>
      <dd className="c-va-tabular">
        {count === undefined || count === 0 ? "—" : `${String(count)} ${count === 1 ? portalText("operations", "answer_25dc282b") : portalText("operations", "answers_e3d9bca2")}`}
      </dd>
    </div>
  );
}

/**
 * plan-021 (review L5): where the transcript is empty — withheld from the reader's role, or never kept — the dividers,
 * the refusals and each VoiceAgent's outcome are still drawn, for a plain and a mixed conversation alike. For a reader
 * whose role withholds transcripts the warm summary is dropped here too, whatever the read carried: it is what was said.
 */
function withheldPassLinesOf(entry: readonly VoiceAgentPassTranscriptEntry<unknown>[] | null, withheld: boolean): ReactNode {
  if (entry === null) return null;
  const line = entry.flatMap((one) => {
    if (one.kind === "item") return [];
    if (one.kind === "divider" && withheld && one.divider.summary !== null) return [{ ...one, divider: { ...one.divider, summary: null } }];
    return [one];
  });
  if (line.length === 0) return null;
  return (
    <div className="c-va-history-lines">
      {line.map((one) => (
        <VoiceAgentPassLine key={one.key} entry={one} />
      ))}
    </div>
  );
}

/** What the Summary section says when there is no summary, in words that are true (review #84). */
export function summaryTextOf(one: Pick<PastConversationView, "summary" | "transcript" | "transcriptWithheld">): string {
  if (one.transcriptWithheld) return portalText("operations", "your_role_does_not_include_transcripts_or_their_summaries_f3a3fd6d");
  if (one.summary !== null) return one.summary;
  if (one.transcript.length === 0) return portalText("operations", "no_summary_nothing_was_kept_of_what_was_said_in_this_conve_16169d45");
  return portalText("operations", "no_summary_has_been_written_for_this_conversation_yet_the_d19962ee");
}

/** The trunk leg's facts, as `<dt>`/`<dd>` pairs inside the detail's facts list (plan-008). */
function SipLegFacts({ leg }: { readonly leg: SipLegSummaryView }): ReactNode {
  usePortalTranslation("operations");
  const origin = sipLegOriginText(leg);
  return (
    <>
      <div>
        <dt>{portalText("operations", "sip_trunk_b97ab9f2")}</dt>
        <dd>{sipLegTrunkText(leg)}</dd>
      </div>
      <div>
        <dt>{portalText("operations", "voice_quality_966ac5c1")}</dt>
        <dd title={HD_SENTENCE()}>{codecWords(leg.codec)}</dd>
        <dd className="c-va-history-fact-note">{HD_SENTENCE()}</dd>
      </div>
      <div>
        <dt>{portalText("operations", "connection_6512ee15")}</dt>
        <dd>{sipLegConnectionText(leg)}</dd>
      </div>
      {origin === null ? null : (
        <div>
          <dt>{portalText("operations", "started_by_19006524")}</dt>
          <dd>{origin}</dd>
        </div>
      )}
    </>
  );
}

/** Whether a pass event names a VoiceAgent none of the stints holds (a refused pass's target): it is read by name then. */
export function voiceAgentPassNamesMissing(
  stint: readonly { readonly voiceAgentId: string }[],
  event: readonly { readonly targetVoiceAgentId: string | null; readonly sourceVoiceAgentId: string | null }[],
): boolean {
  return event.some((one) =>
    [one.targetVoiceAgentId, one.sourceVoiceAgentId].some(
      (named) => named !== null && !stint.some((held) => held.voiceAgentId === named),
    ),
  );
}

/** Each transcript line's VoiceAgent, by the line's index, where the conversation was passed. */
function voiceAgentNameByLineOf(
  plain: readonly VoiceAgentPassTranscriptEntry<{ readonly index: number }>[] | null,
  mixed: readonly VoiceAgentPassTranscriptEntry<MixedTranscriptEntry>[] | null,
): ReadonlyMap<number, string> {
  const named = new Map<number, string>();
  for (const entry of plain ?? []) {
    if (entry.kind === "item" && entry.voiceAgentName !== null) named.set(entry.item.index, entry.voiceAgentName);
  }
  for (const entry of mixed ?? []) {
    if (entry.kind === "item" && entry.item.kind === "line" && entry.voiceAgentName !== null) {
      named.set(entry.item.index, entry.voiceAgentName);
    }
  }
  return named;
}

function ConversationDetail({
  conversationId,
  voiceAgentId,
  pack,
  partyShown,
  onParty,
  surveyAnswer,
}: {
  readonly conversationId: string;
  readonly voiceAgentId: string;
  readonly pack: readonly LanguagePackView[] | undefined;
  /** Whether the list already shows this party's conversations only. */
  readonly partyShown: boolean;
  readonly onParty: (conversationId: string) => void;
  /** A survey conversation: its answers are shown above the summary. */
  readonly surveyAnswer: boolean;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const detail = useQuery(pastConversationQuery(gateway, conversationId));
  // plan-021: a pass event naming a VoiceAgent no stint holds (a refused target) is named from the workspace's list.
  const unnamedVoiceAgent =
    detail.data === undefined ? false : voiceAgentPassNamesMissing(detail.data.voiceAgentStint ?? [], detail.data.voiceAgentPass ?? []);
  const knownVoiceAgent = useQuery({ ...voiceAgentListQuery(gateway), enabled: unnamedVoiceAgent });
  const [copy, setCopy] = useState<"idle" | "copied" | "refused">("idle");
  const reset = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (reset.current !== null) clearTimeout(reset.current);
    },
    [],
  );

  if (detail.data === undefined) {
    return detail.isError ? (
      <p className="c-note c-note-critical" role="alert">
        {portalText("operations", "this_conversation_could_not_be_opened_905d4ece")}{apiErrorText(detail.error, "")}
      </p>
    ) : (
      <p className="c-note">{portalText("operations", "opening_the_conversation_ab81ab9a")}</p>
    );
  }

  const one = detail.data;
  // plan-005 OP-44: only a conversation with parts is drawn as mixed; every other one is unchanged.
  const segment = one.segment ?? [];
  const mixed = segment.length > 0;
  // plan-021 (design §33.13.3): the dividers, the lines' VoiceAgents, the outcomes and the refusals — `null` for a
  // conversation one VoiceAgent answered with no pass event, which is drawn exactly as before.
  const nameOf = voiceAgentNameOf(
    one.voiceAgentStint ?? [],
    (knownVoiceAgent.data ?? []).map((known) => ({ voiceAgentId: known.voiceAgentId, displayName: known.displayName })),
  );
  const languageText = one.languageCode === null ? null : languageName(one.languageCode, pack);
  const voiceAgentPassInput = {
    stint: one.voiceAgentStint ?? [],
    event: one.voiceAgentPass ?? [],
    viewerVoiceAgentId: voiceAgentId,
    nameOf,
    languageText,
  };
  const mixedEntry = mixed ? mixedTranscriptOf(one.transcript, segment) : [];
  const passedMixed = mixed
    ? voiceAgentPassTranscriptOf({ ...voiceAgentPassInput, item: mixedEntry, lineOf: (entry) => (entry.kind === "line" ? entry.line : null) })
    : null;
  const passedPlain = mixed
    ? null
    : voiceAgentPassTranscriptOf({
        ...voiceAgentPassInput,
        item: one.transcript.map((line, index) => ({ line, index })),
        lineOf: (entry) => entry.line,
      });
  const lineVoiceAgentName = voiceAgentNameByLineOf(passedPlain, passedMixed);
  const lineWho = (line: TranscriptLineView, index: number): string =>
    lineVoiceAgentName.get(index) ?? speakerWordOf(line.speaker, one.channelLabel, surveyAnswer);
  const transcriptText = one.transcript
    .map((entry, index) =>
      mixed && entry.mode !== undefined
        ? `${lineWho(entry, index)} (${lineModeWordOf(entry.mode).toLowerCase()}): ${entry.text}`
        : `${lineWho(entry, index)}: ${entry.text}`,
    )
    .join("\n");
  const partyText = partyConversationTextOf(one);
  const trialNote = trialNoteOf(one.billedSeconds, one.chargedSeconds, one.listAmount);
  const talkRateFact = talkRateFactOf(one);
  const mediumSplit = one.mediumSplit ?? null;

  return (
    <div className="c-va-card c-va-history-detail">
      <div className="c-va-history-detail-head">
        <p className="c-va-history-detail-party">{partyLabelOf(one)}</p>
        <OutcomeTag row={one} />
        {one.isTest ? <Tag tone="outline">{portalText("operations", "test_a94a8fe5")}</Tag> : null}
      </div>
      <p className="c-va-history-detail-meta">
        {one.whenText} · {one.conversationId} {portalText("operations", "answered_by_e88cb987")}{" "}
        {one.agentVersionLabel ?? portalText("operations", "an_unrecorded_version_56f2f565")}
      </p>
      {partyText === null || partyShown ? null : (
        <p className="c-va-history-detail-meta">
          <Button
            variant="ghost"
            onClick={() => {
              onParty(one.conversationId);
            }}
          >
            {partyText}
          </Button>
        </p>
      )}

      <div className="c-va-audio">
        {one.recordingUrl !== null || one.recording !== null ? (
          <RecordingPlayer
            conversationId={one.conversationId}
            issuedUrl={one.recordingUrl}
            durationSeconds={one.recording?.durationSeconds ?? one.billedSeconds}
            fileName={recordingFileNameOf(one, one.recording?.blobName ?? null, one.recording?.codec ?? null)}
          />
        ) : (
          <p className="c-va-audio-note">{noRecordingTextOf(one.channelLabel)}</p>
        )}
      </div>

      <dl className="c-va-history-facts">
        <div>
          <dt>{portalText("operations", "channel_879f0b1b")}</dt>
          <dd>{channelLabelWordOf(one.channelLabel)}</dd>
        </div>
        <div>
          <dt>{portalText("operations", "language_89b86ab0")}</dt>
          <dd>
            {languageName(one.languageCode, pack)}
            {isBetaLanguage(pack, one.languageCode) ? (
              <>
                {" "}
                <LanguageBetaTag />
              </>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>{portalText("operations", "billed_763fe55e")}</dt>
          <dd className="c-va-tabular" title={trialNote ?? undefined}>
            {formatDuration(one.billedSeconds)} · {chargedText(one)}
          </dd>
          {talkRateFact === null ? null : <dd className="c-va-history-fact-note">{talkRateFact}</dd>}
          {trialNote === null ? null : <dd className="c-va-history-fact-note">{trialNote}</dd>}
          {mediumSplit === null ? null : (
            <dd className="c-va-history-fact-note">{mediumSplitTextOf(mediumSplit)}</dd>
          )}
        </div>
        {one.sipLeg === undefined || one.sipLeg === null ? null : <SipLegFacts leg={one.sipLeg} />}
        {surveyAnswer ? (
          <AnsweredFact conversationId={one.conversationId} />
        ) : (
          <div>
            <dt>{portalText("operations", "order_value_553cf9a0")}</dt>
            <dd className="c-va-tabular">
              {one.customerOrder === null ? (
                one.outcomeValue === null ? "—" : formatMoney(one.outcomeValue)
              ) : (
                <>
                  {formatMoney(one.customerOrder.total)} ·{" "}
                  <Link
                    to="/orders/$customerOrderReference"
                    params={{ customerOrderReference: one.customerOrder.reference }}
                    search={{ voiceAgent: voiceAgentId }}
                  >
                    {one.customerOrder.reference}
                  </Link>
                </>
              )}
            </dd>
          </div>
        )}
      </dl>

      {/* plan-018 WAPP-B: the request for a person, then every message the caller left, before the summary. */}
      {one.handoff === undefined || one.handoff === null ? null : (
        <>
          <p className="c-va-caps c-va-caps-history">{portalText("operations", "handoff.caps")}</p>
          <HandoffCard handoff={one.handoff} />
        </>
      )}
      {one.takenMessage === undefined || one.takenMessage.length === 0 ? null : (
        <>
          <p className="c-va-caps c-va-caps-history">
            {portalText("operations", "taken_message.caps", {
              count: one.takenMessage.length,
              v0: String(one.takenMessage.length),
            })}
          </p>
          <div className="c-msg-list">
            {one.takenMessage.map((message) => (
              <TakenMessageCard key={message.takenMessageId} message={message} />
            ))}
          </div>
        </>
      )}

      {/* Design sync 2026-10-09: a survey conversation's detail shows its Answers in place of a Summary. */}
      {surveyAnswer ? (
        <ConversationSurveyAnswer conversationId={one.conversationId} />
      ) : (
        <>
          <p className="c-va-caps c-va-caps-history">{portalText("operations", "summary_12b71c3e")}</p>
          <p className="c-va-history-summary">{summaryTextOf(one)}</p>
        </>
      )}

      <p className="c-va-caps c-va-caps-history">{portalText("operations", "transcript_5bcd6022")}</p>
      {one.transcript.length === 0 ? (
        <>
          <p className="c-va-history-summary">
            {one.transcriptWithheld
              ? portalText("operations", "your_role_reads_outcomes_and_analytics_not_what_callers_sa_ffc31939")
              : portalText("operations", "no_transcript_was_kept_for_this_conversation_f0fa7909")}
          </p>
          {/* plan-021: who answered, and how each part ended, even where the words are not shown — a mixed
              (voice and chat) conversation too (review L5). Never the warm summary: it is what was said. */}
          {withheldPassLinesOf(passedPlain ?? passedMixed, one.transcriptWithheld)}
        </>
      ) : mixed ? (
        <div className="c-va-history-lines">
          {(passedMixed ?? mixedEntry.map((item): VoiceAgentPassTranscriptEntry<MixedTranscriptEntry> => ({ kind: "item", item, voiceAgentName: null }))).map(
            (passedEntry) => {
              if (passedEntry.kind !== "item") return <VoiceAgentPassLine key={passedEntry.key} entry={passedEntry} />;
              const entry = passedEntry.item;
              return entry.kind === "switch" ? (
                // A switch reads as a timeline entry does — its time, then what changed.
                <span className="c-va-history-event" key={`switch-${String(entry.segment.ordinal)}`}>
                  <span className="c-va-history-event-at">{formatClock(entry.segment.startedAt)}</span>
                  <span className="c-va-history-event-what">{switchMarkerTextOf(entry.segment)}</span>
                </span>
              ) : (
                <p className="c-va-history-line" key={`${String(entry.index)}-${entry.line.text.slice(0, 16)}`}>
                  <span className={entry.line.speaker === "assistant" ? "c-va-who c-va-who-agent" : "c-va-who"}>
                    {entry.line.mode === undefined ? null : (
                      <Icon
                        glyph={entry.line.mode === "spoken" ? GLYPH.microphone : GLYPH.chatText}
                        size="sm"
                        label={lineModeWordOf(entry.line.mode)}
                      />
                    )}{" "}
                    {passedEntry.voiceAgentName ?? speakerWordOf(entry.line.speaker, one.channelLabel, surveyAnswer)}
                  </span>
                  <span>{entry.line.text}</span>
                </p>
              );
            },
          )}
        </div>
      ) : (
        <div className="c-va-history-lines">
          {(passedPlain ?? one.transcript.map((line, index): VoiceAgentPassTranscriptEntry<{ readonly line: TranscriptLineView; readonly index: number }> => ({ kind: "item", item: { line, index }, voiceAgentName: null }))).map(
            (passedEntry) => {
              if (passedEntry.kind !== "item") return <VoiceAgentPassLine key={passedEntry.key} entry={passedEntry} />;
              const { line: entry, index } = passedEntry.item;
              return (
                <p className="c-va-history-line" key={`${String(index)}-${entry.text.slice(0, 16)}`}>
                  <span className={entry.speaker === "assistant" ? "c-va-who c-va-who-agent" : "c-va-who"}>
                    {passedEntry.voiceAgentName ?? speakerWordOf(entry.speaker, one.channelLabel, surveyAnswer)}
                  </span>
                  <span>{entry.text}</span>
                </p>
              );
            },
          )}
        </div>
      )}

      {/* plan-029 WAPP-HIST2: how the caller confirmed who they were, or that it could not be confirmed. */}
      {one.partyConfirmation === undefined || one.partyConfirmation === null ? null : (
        <p className="c-va-history-summary" data-party-confirmation={one.partyConfirmation.outcome}>
          <Tag status={one.partyConfirmation.outcome === "confirmed" ? "positive" : "caution"}>
            {partyConfirmationSummaryOf(one.partyConfirmation)}
          </Tag>
          {partyConfirmationCountsOf(one.partyConfirmation) === null ? null : (
            <> {partyConfirmationCountsOf(one.partyConfirmation)}</>
          )}
        </p>
      )}

      <p className="c-va-caps c-va-caps-history">{portalText("operations", "what_happened_7661e659")}</p>
      {one.event.length === 0 ? (
        <p className="c-va-history-summary">
          {isTextChannelLabel(one.channelLabel)
            ? portalText("operations", "nothing_was_recorded_beyond_the_conversation_itself_57341658")
            : portalText("operations", "nothing_was_recorded_beyond_the_call_itself_dd8e92e1")}
        </p>
      ) : (
        <div className="c-va-history-events">
          {one.event.map((entry, index) => (
            <span className="c-va-history-event" key={`${String(index)}-${entry.atText}`}>
              <span className="c-va-history-event-at">{entry.atText}</span>
              <span className="c-va-history-event-what">
                {/* plan-021: a pass names its VoiceAgents from the stints and, for a refused target, the workspace's list. */}
                {entry.voiceAgentPass === undefined
                  ? entry.what
                  : (voiceAgentPassSentenceOf(entry.voiceAgentPass, nameOf, languageText) ?? entry.what)}
              </span>
            </span>
          ))}
        </div>
      )}

      <div className="c-va-history-actions">
        {/* plan-029 WAPP-HIST1: offered only where the api says so (a known phone caller, a reader who may block). */}
        {one.mayBlock === true ? <BlockThisNumber conversationId={one.conversationId} voiceAgentId={voiceAgentId} /> : null}
        <Button variant="secondary" disabled>
          {portalText("operations", "flag_for_review_soon_e0784b5b")}</Button>
        <Button
          variant="ghost"
          disabled={one.transcript.length === 0}
          title={one.transcript.length === 0 ? portalText("operations", "there_is_no_transcript_to_copy_c2c7bb29") : undefined}
          onClick={() => {
            if (reset.current !== null) clearTimeout(reset.current);
            void globalThis.navigator.clipboard.writeText(transcriptText).then(
              () => {
                setCopy("copied");
              },
              () => {
                setCopy("refused");
              },
            );
            reset.current = setTimeout(() => {
              setCopy("idle");
            }, COPIED_FOR_MS);
          }}
        >
          {copy === "copied" ? (
            <>
              <Icon glyph={GLYPH.checkCircle} size="md" />
              <span>{portalText("operations", "transcript_copied_2f8f7afc")}</span>
            </>
          ) : (
            portalText("operations", "copy_transcript_58df7ed9")
          )}
        </Button>
        <span className="c-va-history-copy-status" role="status" aria-live="polite">
          {copy === "copied"
            ? portalText("operations", "copied_to_your_clipboard_df327fc1")
            : copy === "refused"
              ? portalText("operations", "your_browser_did_not_allow_copying_select_the_transcript_a_19457937")
              : ""}
        </span>
      </div>
    </div>
  );
}

/** "Showing the calls on +30 21 0123 4567" — the number named from the Numbers list the console already reads. */
function NumberFilterNote({ phoneNumberId, onClear }: { readonly phoneNumberId: string; readonly onClear: () => void }): ReactNode {
  const gateway = useGateway();
  const numbers = useQuery(numberQuery(gateway));
  const e164 = numbers.data?.row.find((one) => one.phoneNumberId === phoneNumberId)?.e164 ?? null;
  return (
    <p className="c-note" role="status">
      {e164 === null
        ? portalText("operations", "history.showing_calls_on_one_number")
        : portalText("operations", "history.showing_calls_on_number", { v0: displayNumberOf(e164) })}{" "}
      <Button variant="ghost" onClick={onClear}>
        {portalText("shell", "merge.contacts.allCalls")}
      </Button>
    </p>
  );
}
